import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import supplierService from '../../../services/supplierService';
import { format } from 'date-fns';

const BuyerFeedbackSection = ({ language, supplierId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const content = {
    en: {
      title: 'Buyer Feedback & Reviews',
      subtitle: 'Customer ratings and reviews with response management',
      overallRating: 'Overall Rating',
      totalReviews: 'Total Reviews',
      productQuality: 'Product Quality',
      communication: 'Communication',
      delivery: 'Delivery',
      verifiedPurchase: 'Verified Purchase',
      respond: 'Respond',
      submitResponse: 'Submit Response',
      cancel: 'Cancel',
      responsePlaceholder: 'Write your response to this review...',
      noReviews: 'No reviews yet',
      responseTime: 'Response Time',
      hours: 'hours',
      supplierResponse: 'Supplier Response',
      respondedOn: 'Responded on'
    },
    zh: {
      title: '买家反馈与评价',
      subtitle: '客户评级和评价及响应管理',
      overallRating: '总体评分',
      totalReviews: '总评价数',
      productQuality: '产品质量',
      communication: '沟通',
      delivery: '交付',
      verifiedPurchase: '已验证购买',
      respond: '回复',
      submitResponse: '提交回复',
      cancel: '取消',
      responsePlaceholder: '写下您对此评价的回复...',
      noReviews: '还没有评价',
      responseTime: '响应时间',
      hours: '小时',
      supplierResponse: '供应商回复',
      respondedOn: '回复于'
    }
  };

  const t = content?.[language];

  useEffect(() => {
    loadReviews();
  }, [supplierId]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const reviewsData = await supplierService?.getSupplierReviews(supplierId);
      setReviews(reviewsData);
    } catch (error) {
      console.error('Failed to load reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = (reviewId) => {
    setRespondingTo(reviewId);
    const review = reviews?.find(r => r?.id === reviewId);
    setResponseText(review?.supplierResponse || '');
  };

  const handleSubmitResponse = async (reviewId) => {
    try {
      setSubmitting(true);
      await supplierService?.respondToReview(reviewId, responseText);
      await loadReviews();
      setRespondingTo(null);
      setResponseText('');
    } catch (error) {
      console.error('Failed to submit response:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const calculateAverages = () => {
    if (reviews?.length === 0) return { overall: 0, product: 0, communication: 0, delivery: 0 };
    
    const sum = reviews?.reduce((acc, review) => ({
      overall: acc?.overall + (review?.rating || 0),
      product: acc?.product + (review?.productQualityRating || 0),
      communication: acc?.communication + (review?.communicationRating || 0),
      delivery: acc?.delivery + (review?.deliveryRating || 0)
    }), { overall: 0, product: 0, communication: 0, delivery: 0 });

    return {
      overall: (sum?.overall / reviews?.length)?.toFixed(1),
      product: (sum?.product / reviews?.length)?.toFixed(1),
      communication: (sum?.communication / reviews?.length)?.toFixed(1),
      delivery: (sum?.delivery / reviews?.length)?.toFixed(1)
    };
  };

  const averages = calculateAverages();

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Icon
            key={star}
            name="Star"
            size={16}
            className={star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-muted'}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-center h-64">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="MessageSquare" size={20} className="text-primary" />
          <h3 className="text-lg font-heading font-semibold text-foreground">{t?.title}</h3>
        </div>
        <p className="text-sm text-muted-foreground">{t?.subtitle}</p>
      </div>

      {/* Rating Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 p-4 bg-muted/30 rounded-lg">
        <div className="text-center">
          <p className="text-3xl font-bold text-foreground">{averages?.overall}</p>
          <div className="flex justify-center my-2">{renderStars(averages?.overall)}</div>
          <p className="text-sm text-muted-foreground">{t?.overallRating}</p>
          <p className="text-xs text-muted-foreground mt-1">{reviews?.length} {t?.totalReviews}</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-foreground">{averages?.product}</p>
          <div className="flex justify-center my-2">{renderStars(averages?.product)}</div>
          <p className="text-sm text-muted-foreground">{t?.productQuality}</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-foreground">{averages?.communication}</p>
          <div className="flex justify-center my-2">{renderStars(averages?.communication)}</div>
          <p className="text-sm text-muted-foreground">{t?.communication}</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-foreground">{averages?.delivery}</p>
          <div className="flex justify-center my-2">{renderStars(averages?.delivery)}</div>
          <p className="text-sm text-muted-foreground">{t?.delivery}</p>
        </div>
      </div>

      {/* Reviews List */}
      {reviews?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="MessageSquare" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">{t?.noReviews}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews?.map((review) => (
            <div key={review?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name="User" size={20} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">
                        {review?.buyer?.fullName || 'Anonymous'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(review?.createdAt), 'MMM dd, yyyy')}
                      </p>
                    </div>
                    {review?.isVerifiedPurchase && (
                      <span className="text-xs bg-success/10 text-success px-2 py-1 rounded">
                        {t?.verifiedPurchase}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mb-2">
                    {renderStars(review?.rating)}
                    {review?.responseTimeHours && (
                      <span className="text-xs text-muted-foreground">
                        {t?.responseTime}: {Number(review?.responseTimeHours)?.toFixed(1)} {t?.hours}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-foreground mb-3">
                {language === 'en' ? review?.reviewTextEn : review?.reviewTextZh}
              </p>

              {/* Supplier Response */}
              {review?.supplierResponse && respondingTo !== review?.id ? (
                <div className="bg-muted/30 rounded-lg p-3 mt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Reply" size={16} className="text-primary" />
                    <span className="text-sm font-medium text-foreground">{t?.supplierResponse}</span>
                    {review?.respondedAt && (
                      <span className="text-xs text-muted-foreground">
                        {t?.respondedOn} {format(new Date(review?.respondedAt), 'MMM dd, yyyy')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-foreground">{review?.supplierResponse}</p>
                </div>
              ) : null}

              {/* Response Form */}
              {respondingTo === review?.id ? (
                <div className="mt-3 space-y-3">
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e?.target?.value)}
                    placeholder={t?.responsePlaceholder}
                    className="w-full px-3 py-2 border border-border rounded-md text-sm min-h-[100px]"
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleSubmitResponse(review?.id)}
                      loading={submitting}
                    >
                      {t?.submitResponse}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setRespondingTo(null);
                        setResponseText('');
                      }}
                    >
                      {t?.cancel}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  iconName="Reply"
                  onClick={() => handleRespond(review?.id)}
                  className="mt-3"
                >
                  {review?.supplierResponse ? 'Edit Response' : t?.respond}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BuyerFeedbackSection;